// Import Types
import type {
    DataWithPaging,
    ListCustomFields,
    WorkflowGeneralDetails,
    WorkflowPreview,
    WorkflowRuntimeState,
    WorkflowSchema,
    WorkflowVisualization,
    WorkflowAwardBlocksReport,
    VisualizationIcon,
    VisualizationButtonAction,
} from 'types';
// Import Helpers
import { generateCleanUrlParams } from 'services/Helpers';
// Import Config
import {
    CRMRequestService,
    CRMRequestDownloadLinkService,
    postCancelableRequest,
    deleteCancelableRequest,
    getCancelableRequest,
} from './http.config';

const CustomerJourneyHttpService = {
    getCustomerJourneyList: () => {
        // TODO: create a new type for the response model instead of using AnyObject
        return postCancelableRequest<DataWithPaging<AnyObject<{ CustomFields: Nullable<ListCustomFields> }>>>({
            requestService: CRMRequestService,
            endpointPath: () => '/Workflow/List',
        });
    },
    deleteCustomerJourney: () => {
        return deleteCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId }) => `/Workflow/Delete?id=${workflowId}`,
        });
    },
    deleteCustomerJourneys: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Workflow/Delete/`,
        });
    },
    archiveCustomerJourney: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId }) => `Workflow/Archive?id=${workflowId}`,
        });
    },
    unarchiveCustomerJourney: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId }) => `/Workflow/Unarchive?id=${workflowId}`,
        });
    },
    archiveCustomerJourneys: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `Workflow/Archive`,
        });
    },
    unarchiveCustomerJourneys: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Workflow/Unarchive`,
        });
    },
    stopSendCustomerJourney: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId }) => `Workflow/Suspend?id=${workflowId}`,
        });
    },
    startCustomerJourney: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId }) => `Workflow/Start?id=${workflowId}`,
        });
    },
    getCustomerJourneySchema: () => {
        return getCancelableRequest<WorkflowSchema>({
            requestService: CRMRequestService,
            endpointPath: ({ promotionType }) => `Workflow/Schema?promotionType=${promotionType}`,
        });
    },
    getCustomerJourneyPreviewById: () => {
        return getCancelableRequest<WorkflowPreview>({
            requestService: CRMRequestService,
            defaultParams: { isClone: false },
            endpointPath: ({ workflowId, isClone, pointInTime }) =>
                `Workflow/Preview${generateCleanUrlParams({ id: workflowId, isClone, pointInTime })}`,
        });
    },
    getCustomerJourneyClientListById: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `Workflow/GetBlockReport`,
        });
    },
    createCustomerJourney: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `Workflow/Create`,
        });
    },
    setCustomerJourneyBlockWorkingState: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `Workflow/SetBlockWorkingState`,
        });
    },
    editCustomerJourney: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId }) => `Workflow/Update?id=${workflowId}`,
        });
    },
    getCustomerJourneyClientsCounts: () => {
        return postCancelableRequest<WorkflowRuntimeState>({
            requestService: CRMRequestService,
            endpointPath: () => `Workflow/State`,
        });
    },
    downloadWorkflowAllReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestDownloadLinkService,
            endpointPath: () => `Workflow/DownloadReport`,
        });
    },
    downloadWorkflowBlockReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestDownloadLinkService,
            endpointPath: () => `Workflow/DownloadWorkflowBlockReport`,
        });
    },
    getCustomerJourneyTimeline: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId, pointInTime }) =>
                `Workflow/GetTimeline${generateCleanUrlParams({ workflowId, pointInTime })}`,
        });
    },
    resumeCustomerJourney: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId }) => `Workflow/Resume?id=${workflowId}`,
        });
    },
    getCustomerJourneyGeneralDetails: () => {
        return getCancelableRequest<WorkflowGeneralDetails>({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId, pointInTime }) =>
                `Workflow/GetGeneralDetails${generateCleanUrlParams({ id: workflowId, pointInTime })}`,
        });
    },
    createCustomerJourneyVisualization: () => {
        return postCancelableRequest<WorkflowVisualization>({
            requestService: CRMRequestService,
            endpointPath: () => 'Visualization/CreateWorkflowVisualization',
        });
    },
    editCustomerJourneyVisualization: () => {
        return postCancelableRequest<WorkflowVisualization>({
            requestService: CRMRequestService,
            endpointPath: () => 'Visualization/UpdateWorkflowVisualization',
        });
    },
    getCustomerJourneyVisualizationById: () => {
        return getCancelableRequest<WorkflowVisualization>({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId, pointInTime }) =>
                `Visualization/GetWorkflowVisualization${generateCleanUrlParams({ id: workflowId, pointInTime })}`,
        });
    },
    getVisualizationIcons: () => {
        return getCancelableRequest<VisualizationIcon[], { pointInTime?: Nullable<string> }>({
            requestService: CRMRequestService,
            endpointPath: ({ pointInTime }) =>
                `Visualization/GetVisualizationIcons${generateCleanUrlParams({ pointInTime })}`,
        });
    },
    getVisualizationButtonActions: () => {
        return getCancelableRequest<VisualizationButtonAction[], { pointInTime?: Nullable<string> }>({
            requestService: CRMRequestService,
            endpointPath: ({ pointInTime }) =>
                `Visualization/GetVisualizationButtonActions${generateCleanUrlParams({ pointInTime })}`,
        });
    },
    getBlockReportConfigs: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId }) => `Workflow/GetBlockReportConfigs?workflowId=${workflowId}`,
        });
    },
    getBlockReportDefaultConfigs: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => 'Workflow/GetBlockReportDefaultConfigs',
        });
    },
    saveBlockReportConfig: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => 'Workflow/SaveBlockReportConfig',
        });
    },
    getTotals: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `Workflow/GetTotals`,
        });
    },
    sendToEmailWorkflowAllReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestDownloadLinkService,
            endpointPath: () => `Workflow/GenerateReport`,
        });
    },
    sendToEmailWorkflowBlockReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestDownloadLinkService,
            endpointPath: () => `Workflow/GenerateBlockReport`,
        });
    },
    getCustomerJourneyLimits: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `Workflow/GetLimits`,
        });
    },
    getTemplateModels: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ templateId }) => `Workflow/GetTemplateModels?templateId=${templateId}`,
        });
    },
    getWorkflowAwardBlocksReport: () => {
        return getCancelableRequest<WorkflowAwardBlocksReport>({
            requestService: CRMRequestService,
            endpointPath: ({ workflowId }) =>
                `Workflow/GetWorkflowAwardBlocksReport${generateCleanUrlParams({ workflowId })}`,
        });
    },
};

export default CustomerJourneyHttpService;
